import React, { useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './Button';
import { Input } from './Input';
import { Select } from './Select';
import { Invoice } from './InvoiceCard';
interface EditInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (invoice: Invoice) => void;
  invoice: Invoice | null;
}
export function EditInvoiceModal({
  isOpen,
  onClose,
  onSave,
  invoice
}: EditInvoiceModalProps) {
  const [supplier, setSupplier] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [date, setDate] = useState('');
  const [category, setCategory] = useState<'Sikkerhed' | 'Værktøj' | 'Materialer' | 'Andet'>('Materialer');
  const [type, setType] = useState<'Ladeboks' | 'Andet'>('Ladeboks');
  const [amount, setAmount] = useState('');
  const [paid, setPaid] = useState('no');
  const [notes, setNotes] = useState('');
  useEffect(() => {
    if (invoice) {
      setSupplier(invoice.supplier);
      setInvoiceNumber(invoice.invoiceNumber);
      setDate(invoice.date);
      setCategory(invoice.category);
      setType(invoice.type || 'Ladeboks');
      setAmount(invoice.amount.toString());
      setPaid(invoice.paid ? 'yes' : 'no');
      setNotes(invoice.notes || '');
    }
  }, [invoice]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!invoice || !supplier.trim() || !invoiceNumber.trim() || !date || !amount) return;
    onSave({
      ...invoice,
      supplier,
      invoiceNumber,
      date,
      category,
      type,
      amount: parseFloat(amount),
      paid: paid === 'yes',
      notes: notes || undefined
    });
    onClose();
  };
  if (!invoice) return null;
  return <AnimatePresence data-id="element-584">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={onClose} data-id="element-585" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        duration: 0.3
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" data-id="element-586">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 my-8" data-id="element-587">
              <div className="flex items-center justify-between mb-6" data-id="element-588">
                <h2 className="text-2xl font-semibold text-stone-900" data-id="element-589">
                  Rediger faktura
                </h2>
                <button onClick={onClose} className="text-stone-400 hover:text-stone-600 transition-colors" aria-label="Luk" data-id="element-590">
                  <XIcon className="w-6 h-6" data-id="element-591" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-592">
                <div data-id="element-593">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-594">
                    Faktura information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-595">
                    <Input label="Leverandør" value={supplier} onChange={e => setSupplier(e.target.value)} placeholder="Indtast leverandør" required data-id="element-596" />

                    <Input label="Faktura nr." value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="f.eks. INV-2024-001" required data-id="element-597" />

                    <Input label="Dato" type="date" value={date} onChange={e => setDate(e.target.value)} required data-id="element-598" />

                    <Select label="Type" value={type} onChange={e => setType(e.target.value as 'Ladeboks' | 'Andet')} options={[{
                  value: 'Ladeboks',
                  label: 'Ladeboks'
                }, {
                  value: 'Andet',
                  label: 'Andet'
                }]} required data-id="element-599" />

                    <Select label="Kategori" value={category} onChange={e => setCategory(e.target.value as 'Sikkerhed' | 'Værktøj' | 'Materialer' | 'Andet')} options={[{
                  value: 'Sikkerhed',
                  label: 'Sikkerhed'
                }, {
                  value: 'Værktøj',
                  label: 'Værktøj'
                }, {
                  value: 'Materialer',
                  label: 'Materialer'
                }, {
                  value: 'Andet',
                  label: 'Andet'
                }]} required data-id="element-600" />

                    <Input label="Beløb (kr)" type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="f.eks. 1500" required data-id="element-601" />

                    <Select label="Betalt" value={paid} onChange={e => setPaid(e.target.value)} options={[{
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'yes',
                  label: 'Ja'
                }]} data-id="element-602" />
                  </div>

                  <div className="mt-4" data-id="element-603">
                    <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-604">
                      Noter
                    </label>
                    <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Tilføj noter om denne faktura" rows={3} className="w-full px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none" data-id="element-605" />
                  </div>
                </div>

                <div className="flex gap-3 pt-2" data-id="element-606">
                  <Button type="button" variant="secondary" onClick={onClose} className="flex-1" data-id="element-607">
                    Annuller
                  </Button>
                  <Button type="submit" variant="primary" className="flex-1" data-id="element-608">
                    Gem ændringer
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}